/**
 * Sites & keys — one partner, several brands or properties. Each site is a
 * live/test key pair with its own config and its own "where your key is used"
 * list (KeyUsage.tsx: open by default, optional lock, per-website block). Key rotation (O,A)
 * issues a new key and keeps the old one working for 7 days, because the
 * snippet has to be redeployed on pages the partner may not touch daily.
 *
 * The FinderlyFix-hosted "Widget page" (HostedPagePanel.tsx) is edited here too: it
 * is the website that stands in for the partner's own, so it lives with the
 * other places the key runs (owner/admin only, like the API).
 *
 * Pause widget (owners, admins, developers) switches the live key and its
 * test twin off without deleting anything: the widget and any widget page
 * using the key stop showing within about a minute. Resume turns them back on.
 *
 * Below 720 px the table becomes one card per site (the Leads inbox's
 * `.cards` pattern): a 40-character key in a table column squeezed to one
 * character per line at 390 px, so each key gets its own wrapping line.
 */
import { useEffect, useId, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { useSites, type SitePair } from '../state/sites';
import { useLastSeen } from '../state/lastSeen';
import { FOCUS_NEW_SITE_EVENT, NEW_SITE_HASH } from '../state/newSite';
import { CopyButton, Empty, ErrorNotice, Field, Loading, Modal, PageHead, Time, formatDate, formatDateTime, useToast } from '../ui/kit';
import { useLocalState, useMutation, usePhone, useQuery } from '../ui/hooks';
import { HostedPagePanel, loadHostedPages } from './HostedPagePanel';
import { KeyUsage } from './KeyUsage';
import { WIDGET_PAGE_ANCHOR, WIDGET_PAGE_PATH } from './WidgetPageShare';

export function Sites() {
  const { api, role } = useSession();
  const { pairs, current, select, reload, loading } = useSites();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [rotating, setRotating] = useState<string | null>(null);
  const rotatingPair = pairs.find((p) => p.live.site_key === rotating) ?? null;
  const [rotated, setRotated] = useState<{ key: string; grace: string } | null>(null);
  const create = useMutation((l: string) => api.createSite(l));
  const rotate = useMutation((key: string) => api.rotateSite(key));
  const [pausing, setPausing] = useState<string | null>(null);
  const pausingPair = pairs.find((p) => p.live.site_key === pausing) ?? null;
  const pause = useMutation((key: string) => api.pauseSite(key));
  const resume = useMutation((key: string) => api.resumeSite(key));
  const canPause = can(role, 'pauseSite');
  const doResume = async (p: SitePair) => {
    if (await resume.run(p.live.site_key)) {
      await reload();
      toast(`Widget resumed for ${p.live.label}`);
    }
  };
  const pauseButton = (p: SitePair) =>
    !canPause ? null : p.live.paused_at ? (
      <button type="button" className="btn btn-sm" disabled={resume.pending} aria-label={`Resume widget for ${p.live.label}`} onClick={() => void doResume(p)}>
        Resume widget
      </button>
    ) : (
      <button
        type="button"
        className="btn btn-sm btn-danger-subtle"
        aria-label={`Pause widget for ${p.live.label}…`}
        onClick={() => {
          pause.reset();
          setPausing(p.live.site_key);
        }}
      >
        Pause widget…
      </button>
    );
  const phone = usePhone();
  const hosted = useHostedDisclosure();
  const lastSeen = useLastSeen();
  const newSiteRef = useRef<HTMLInputElement>(null);

  // "+ New site" in the top bar (state/newSite.ts): `/sites#new` focuses the
  // field. Deferred a tick so it lands after the Shell moves focus to the
  // page heading on navigation.
  useEffect(() => {
    let timer: number | undefined;
    const focus = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const el = newSiteRef.current;
        if (!el) return;
        el.scrollIntoView?.({ block: 'center' });
        el.focus({ preventScroll: true });
      }, 0);
    };
    if (window.location.hash === NEW_SITE_HASH) focus();
    window.addEventListener(FOCUS_NEW_SITE_EVENT, focus);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(FOCUS_NEW_SITE_EVENT, focus);
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await create.run(label.trim());
    if (res) {
      setLabel('');
      await reload();
      select(res.live.site_key);
      toast(`Created ${res.live.label}`);
    }
  };

  return (
    <div>
      <PageHead
        title="Sites & keys"
        lede="Each site has a live key and a test key. By default a key works on any website where you paste the snippet."
      />
      <div className="stack-lg">
        <section className="panel">
          {loading ? (
            <Loading />
          ) : pairs.length && !phone ? (
            <div className="table-wrap">
              <table className="data">
                <caption className="visually-hidden">Sites</caption>
                <thead>
                  <tr>
                    <th scope="col">Site</th>
                    <th scope="col">Live key</th>
                    <th scope="col">Test key</th>
                    <th scope="col" className="num">Websites</th>
                    <th scope="col">Last impression</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pairs.map((p) => (
                    <tr key={p.live.site_key} aria-current={current?.live.site_key === p.live.site_key ? 'true' : undefined}>
                      <td>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ fontWeight: 700, color: 'var(--ink)', padding: 0 }} onClick={() => select(p.live.site_key)}>
                          {p.live.label}
                        </button>
                        {current?.live.site_key === p.live.site_key ? <span className="tag accent" style={{ marginLeft: 6 }}>selected</span> : null}
                        <PausedTag pausedAt={p.live.paused_at} style={{ marginLeft: 6 }} />
                      </td>
                      <td>
                        <span className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                          <code className="key">{p.live.site_key}</code>
                          <CopyButton text={p.live.site_key} className="btn btn-ghost btn-sm" />
                        </span>
                      </td>
                      <td>
                        {p.test ? (
                          <span className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                            <code className="key">{p.test.site_key}</code>
                            <CopyButton text={p.test.site_key} className="btn btn-ghost btn-sm" />
                          </span>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td className="num">{p.live.origins_count}</td>
                      <td>
                        <Time iso={lastSeen.site(p.live.site_key, p.live.last_impression_at)} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="row" style={{ gap: 6, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                          {pauseButton(p)}
                          {can(role, 'rotateSiteKey') ? (
                            <button type="button" className="btn btn-sm" aria-label={`Rotate live key for ${p.live.label}…`} onClick={() => setRotating(p.live.site_key)}>
                              Rotate live key…
                            </button>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : pairs.length ? (
            <div className="cards">
              {pairs.map((p) => {
                const selected = current?.live.site_key === p.live.site_key;
                return (
                  <div className="card" key={p.live.site_key} aria-current={selected ? 'true' : undefined}>
                    <span className="row" style={{ gap: 6 }}>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ fontWeight: 700, color: 'var(--ink)', padding: 0, whiteSpace: 'normal', textAlign: 'left' }} onClick={() => select(p.live.site_key)}>
                        {p.live.label}
                      </button>
                      {selected ? <span className="tag accent">selected</span> : null}
                      <PausedTag pausedAt={p.live.paused_at} />
                    </span>
                    <span className="card-label">Live key</span>
                    <span className="keyline">
                      <code className="key">{p.live.site_key}</code>
                      <CopyButton text={p.live.site_key} className="btn btn-sm" label="Copy" />
                    </span>
                    <span className="card-label">Test key</span>
                    {p.test ? (
                      <span className="keyline">
                        <code className="key">{p.test.site_key}</code>
                        <CopyButton text={p.test.site_key} className="btn btn-sm" label="Copy" />
                      </span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                    <span className="small muted">
                      {p.live.origins_count} {p.live.origins_count === 1 ? 'website' : 'websites'} · last impression <Time iso={lastSeen.site(p.live.site_key, p.live.last_impression_at)} />
                    </span>
                    {can(role, 'rotateSiteKey') || canPause ? (
                      <span className="card-actions">
                        {pauseButton(p)}
                        {can(role, 'rotateSiteKey') ? (
                          <button type="button" className="btn btn-sm" aria-label={`Rotate live key for ${p.live.label}…`} onClick={() => setRotating(p.live.site_key)}>
                            Rotate live key…
                          </button>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty title="No sites yet" />
          )}
          {resume.error ? (
            <div className="panel-body">
              <ErrorNotice error={resume.error} />
            </div>
          ) : null}
          <form className="panel-body row" style={{ alignItems: 'flex-end', borderTop: '1px solid var(--line)' }} onSubmit={submit}>
            <div className="grow">
              <Field label="New site" hint="A brand, property or microsite. You get a live and a test key.">
                {(p) => <input {...p} ref={newSiteRef} className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Cascade — resident portal" maxLength={200} />}
              </Field>
            </div>
            <button type="submit" className="btn btn-primary" disabled={create.pending || !label.trim()}>
              {create.pending ? 'Creating…' : 'Create site'}
            </button>
          </form>
          {create.error ? (
            <div className="panel-body" style={{ paddingTop: 0 }}>
              <ErrorNotice error={create.error} />
            </div>
          ) : null}
        </section>

        {current ? (
          <section className="stack">
            <SiteName key={current.live.site_key} liveKey={current.live.site_key} label={current.live.label} />
            {current.live.paused_at ? (
              <div className="notice warn small" role="status">
                The widget is paused on this site’s live and test keys since {formatDate(current.live.paused_at)}. It doesn’t show on any
                website or widget page until you resume it.
              </div>
            ) : null}
            <KeyUsage
              siteKey={current.live.site_key}
              testKey={current.test?.site_key ?? null}
              policy={current.live.origin_policy ?? 'any'}
            />
          </section>
        ) : null}

        {can(role, 'hostedPage') ? (
          <section id={WIDGET_PAGE_ANCHOR} ref={hosted.ref} tabIndex={-1} className="stack" aria-labelledby="hosted-page-title">
            <div className="stack-sm" style={{ gap: 2 }}>
              <h2 id="hosted-page-title">Pages from us</h2>
              <p className="small muted" style={{ margin: 0 }}>
                Pages we host at finderlyfix.com/p/… for your business, or for a link and QR code to share. Up to 3, e.g. one per brand,
                service area or campaign. They have their own place too: <Link to={WIDGET_PAGE_PATH}>Your page</Link>.
              </p>
            </div>
            {hosted.collapsed ? (
              <div className="row" style={{ flexWrap: 'wrap' }}>
                <span className="small muted">You’re installing the widget on your own website, so this is optional.</span>
                <button type="button" className="btn btn-sm" aria-expanded={false} aria-controls={hosted.panelId} onClick={() => hosted.setOpen(true)}>
                  Set up a page
                </button>
              </div>
            ) : (
              <div id={hosted.panelId} className="stack-sm">
                {hosted.collapsible ? (
                  <div>
                    <button type="button" className="btn btn-sm btn-ghost" aria-expanded aria-controls={hosted.panelId} onClick={() => hosted.setOpen(false)}>
                      Hide page options
                    </button>
                  </div>
                ) : null}
                <HostedPagePanel />
              </div>
            )}
          </section>
        ) : null}
      </div>

      <Modal
        open={Boolean(rotating)}
        title={`Rotate the live key for ${rotatingPair?.live.label ?? 'this site'}?`}
        onClose={() => setRotating(null)}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setRotating(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={rotate.pending}
              onClick={async () => {
                const res = await rotate.run(rotating ?? '');
                setRotating(null);
                if (res) {
                  setRotated({ key: res.new_site_key, grace: res.grace_until });
                  await reload();
                  select(res.new_site_key);
                }
              }}
            >
              {rotate.pending ? 'Rotating…' : 'Rotate live key'}
            </button>
          </>
        }
      >
        <p>
          This replaces the <strong>live key</strong> <code className="key">{rotating}</code>. You get a new live key with the same websites and settings,
          and the old one keeps working for 7 days so you can update your pages.
        </p>
        <p className="small muted">
          {rotatingPair?.test ? (
            <>
              The test key <code className="key">{rotatingPair.test.site_key}</code> doesn’t change.
            </>
          ) : (
            'The test key doesn’t change.'
          )}
        </p>
        <ErrorNotice error={rotate.error} />
      </Modal>
      <Modal
        open={Boolean(pausing)}
        title={`Pause the widget for ${pausingPair?.live.label ?? 'this site'}?`}
        onClose={() => setPausing(null)}
        footer={
          <>
            <button type="button" className="btn" autoFocus onClick={() => setPausing(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={pause.pending}
              onClick={async () => {
                const res = await pause.run(pausing ?? '');
                if (!res) return;
                setPausing(null);
                await reload();
                toast(`Widget paused for ${pausingPair?.live.label ?? 'this site'}`);
              }}
            >
              {pause.pending ? 'Pausing…' : 'Pause widget'}
            </button>
          </>
        }
      >
        <p>
          The widget stops showing on every website that uses this key, and on widget pages that use it, within a minute. Its test key
          is paused too.
        </p>
        <p className="small muted">Nothing is deleted; you can resume at any time.</p>
        <ErrorNotice error={pause.error} />
      </Modal>
      <Modal
        open={Boolean(rotated)}
        title="New live key issued"
        onClose={() => setRotated(null)}
        footer={
          <button type="button" className="btn btn-primary" onClick={() => setRotated(null)}>
            Done
          </button>
        }
      >
        <div className="secret">{rotated?.key}</div>
        <p className="small">The old key stops working {formatDateTime(rotated?.grace)}. Update the snippet on every page before then.</p>
      </Modal>
    </div>
  );
}

/**
 * The selected site's name, with Rename for owners and admins (third live
 * review, 2026-09-27: a site created as "Finderly" could not be renamed).
 * Renaming the live key renames its test twin too, server-side. The name is
 * the portal's only: the widget shows the organisation's name (Organisation
 * → Name), not a site's.
 */
function SiteName({ liveKey, label }: { liveKey: string; label: string }) {
  const { api, role } = useSession();
  const { reload } = useSites();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label);
  const rename = useMutation((next: string) => api.renameSite(liveKey, next));
  const trimmed = value.trim();
  const issue = editing && !trimmed ? [{ path: 'label', message: 'Enter a name for this site.' }] : [];
  const headingId = useId();
  if (!editing) {
    return (
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <h2 id={headingId} className="wrap-anywhere" style={{ flex: '0 1 auto' }}>
          {label}
        </h2>
        {can(role, 'renameSite') ? (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            aria-describedby={headingId}
            onClick={() => {
              setValue(label);
              rename.reset();
              setEditing(true);
            }}
          >
            Rename
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <form
      className="panel panel-pad stack-sm"
      aria-label="Rename site"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!trimmed) return;
        if (trimmed === label) {
          setEditing(false);
          return;
        }
        if (await rename.run(trimmed)) {
          await reload();
          setEditing(false);
          toast(`Renamed to ${trimmed}`);
        }
      }}
    >
      <Field label="Site name" hint="Only you and your team see it. Its test key is renamed to match. Homeowners see your organisation’s name." issues={issue}>
        {(p) => <input {...p} className="input" autoFocus value={value} maxLength={200} onChange={(e) => setValue(e.target.value)} />}
      </Field>
      <ErrorNotice error={rename.error} />
      <div className="row" style={{ gap: 8 }}>
        <button type="submit" className="btn btn-primary btn-sm" disabled={rename.pending || !trimmed}>
          {rename.pending ? 'Saving…' : 'Save name'}
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The Hosted page form is for partners with no website of their own. Someone
 * who answered "I can" (or "Someone else does") on "Who installs it" sees it
 * collapsed behind a disclosure — unless a page already exists, which is then
 * shown open because they are using it. The answer is the per-browser one
 * InstallOwner.tsx stores; "I can" is also that screen's default.
 */
function useHostedDisclosure() {
  const { api, org, role } = useSession();
  const panelId = useId();
  const [installer] = useLocalState<string>(`fx-installer-${org?.id}`, 'self');
  const pages = useQuery(() => loadHostedPages(api), [], { enabled: can(role, 'hostedPage') });
  // Arriving from the Dashboard's "Share your widget page" (`/sites#widget-page`)
  // opens the form and brings it into view.
  const fromLink = window.location.hash === `#${WIDGET_PAGE_ANCHOR}`;
  const [open, setOpen] = useState<boolean | null>(fromLink ? true : null);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!fromLink) return;
    const timer = window.setTimeout(() => {
      ref.current?.scrollIntoView?.({ block: 'start' });
      ref.current?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [fromLink]);
  // Undecided (still loading, or the read failed) counts as "show it": the
  // panel then shows its own loading or error state.
  const collapsible = installer !== 'none' && !pages.loading && !pages.error && !pages.data?.items.some((p) => p.enabled);
  return { panelId, ref, collapsible, collapsed: collapsible && open !== true, setOpen };
}

/** "Paused since <date>" on a paused key pair; nothing while it runs. */
function PausedTag({ pausedAt, style }: { pausedAt: string | null | undefined; style?: CSSProperties }) {
  if (!pausedAt) return null;
  return (
    <span className="tag warn" style={style} title={formatDateTime(pausedAt)}>
      Paused since {formatDate(pausedAt)}
    </span>
  );
}
